import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { type KanbanTask } from "@/components/ui/trello-kanban-board";
import {
  Link2,
  X,
  Plus,
  Upload,
  User,
  ChevronDown,
  Trash2,
  Settings2,
  Film,
  HardDrive,
  CalendarDays,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { type PipelineSocialPlatform } from "@shared/pipeline-social-platforms";
import {
  PIPELINE_NOTES_MAX_LENGTH,
  PIPELINE_REFERENCE_URL_MAX_LENGTH,
  isPipelineReferenceUrl,
} from "@shared/content-pipeline-fields";
import { PIPELINE_SOCIAL_PLATFORMS } from "@/lib/pipeline-social-platforms";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, uploadFile } from "@/lib/queryClient";
import { VideoReviewTab } from "@/components/video-review-tab";
import CloudStoragePicker, {
  type ImportedFile,
  type SelectedCloudFile,
} from "@/components/cloud-storage-picker";

// ── Types ─────────────────────────────────────────────────

interface WorkspaceMember {
  id: string;
  email: string;
  username?: string;
}

interface CloudStorageStatusResponse {
  google_drive: { connected: boolean };
  dropbox: { connected: boolean };
}

interface RemoteImportedMedia {
  url: string;
  mediaType: "image" | "video";
  originalName: string;
  source: "remote";
}

type ManualCloudVideoLink = {
  provider: "google_drive" | "dropbox";
  fileId?: string;
};

export interface PipelineItemDrawerProps {
  task: KanbanTask | null;
  open: boolean;
  onClose: () => void;
  onSave: (updated: KanbanTask) => void;
  onDuplicate?: (source: KanbanTask) => void;
  onDelete?: (taskId: string) => void;
  availableContentTypes: string[];
  onAddContentType: (tag: string) => void;
  onRemoveContentType: (tag: string) => void;
}

// ── Constants ─────────────────────────────────────────────

const PROCESS_OPTIONS = [
  { value: "pomysl", label: "Pomysł", color: "bg-slate-500" },
  { value: "scenariusz", label: "Do napisania / scenariusz", color: "bg-violet-500" },
  { value: "nagranie", label: "Do nagrania", color: "bg-blue-500" },
  { value: "montaz", label: "Do montażu", color: "bg-indigo-500" },
  { value: "gotowe", label: "Gotowe do publikacji", color: "bg-cyan-500" },
  { value: "opublikowane", label: "Opublikowane", color: "bg-emerald-500" },
  { value: "nie-wyszlo", label: "Nie wyszło", color: "bg-red-500" },
  { value: "evergreen", label: "Evergreen", color: "bg-amber-500" },
];

const REFERENCE_URL_ERROR = "Podaj pełny link zaczynający się od http:// lub https://";

// Konwersja ISO → format datetime-local input (YYYY-MM-DDTHH:mm)
const toDatetimeLocal = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toDriveFileUrl = (fileId: string): string => `https://drive.google.com/file/d/${fileId}/view`;

const getDriveFileId = (url?: string): string | null => {
  if (!url) return null;
  const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (fileIdMatch) return fileIdMatch[1];
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return openMatch[1];
  return null;
};

const toDriveImagePreviewUrl = (url: string): string => {
  const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (fileIdMatch) return `https://drive.google.com/thumbnail?id=${fileIdMatch[1]}&sz=w1600`;
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1600`;
  return url;
};

const resolveImagePreviewUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (url.includes("drive.google.com")) return toDriveImagePreviewUrl(url);
  return url;
};

const isDropboxUrl = (url?: string): boolean => Boolean(url?.includes("dropbox.com"));

const getManualCloudVideoLink = (url?: string): ManualCloudVideoLink | null => {
  const driveFileId = getDriveFileId(url);
  if (driveFileId) {
    return { provider: "google_drive", fileId: driveFileId };
  }

  if (isDropboxUrl(url)) {
    return { provider: "dropbox" };
  }

  return null;
};

const getCloudVideoLabel = (provider: ManualCloudVideoLink["provider"]): string =>
  provider === "google_drive" ? "Google Drive" : "Dropbox";

const isYouTubeUrl = (url?: string): boolean =>
  Boolean(url && (url.includes("youtube.com") || url.includes("youtu.be")));

const getResponseText = async (res: Response): Promise<string> => {
  try {
    return await res.text();
  } catch {
    return "";
  }
};

const THUMBNAIL_CAPTURE_SECONDS = 1.25;

const captureVideoThumbnail = async (videoUrl: string): Promise<File> => {
  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = "anonymous";
  video.src = videoUrl;

  await new Promise<void>((resolve, reject) => {
    const handleLoadedMetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const seekTarget =
        duration > 0 ? Math.min(THUMBNAIL_CAPTURE_SECONDS, Math.max(0.1, duration / 3)) : 0.1;

      const handleSeeked = () => {
        cleanup();
        resolve();
      };

      const handleError = () => {
        cleanup();
        reject(new Error("Failed to capture frame from video."));
      };

      const cleanup = () => {
        video.removeEventListener("seeked", handleSeeked);
        video.removeEventListener("error", handleError);
      };

      video.addEventListener("seeked", handleSeeked, { once: true });
      video.addEventListener("error", handleError, { once: true });

      try {
        video.currentTime = seekTarget;
      } catch {
        cleanup();
        reject(new Error("Failed to seek to thumbnail capture position."));
      }
    };

    const handleError = () => {
      cleanup();
      reject(new Error("Failed to load video for thumbnail generation."));
    };

    const cleanup = () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("error", handleError);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata, { once: true });
    video.addEventListener("error", handleError, { once: true });
  });

  const canvas = document.createElement("canvas");
  const width = video.videoWidth || 1280;
  const height = video.videoHeight || 720;
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to prepare canvas for thumbnail.");
  }

  ctx.drawImage(video, 0, 0, width, height);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
          return;
        }
        reject(new Error("Failed to save thumbnail."));
      },
      "image/jpeg",
      0.88
    );
  });

  return new File([blob], `video-thumbnail-${Date.now()}.jpg`, { type: "image/jpeg" });
};

// ── Component ─────────────────────────────────────────────

export function PipelineItemDrawer({
  task,
  open,
  onClose,
  onSave,
  onDuplicate,
  onDelete,
  availableContentTypes,
  onAddContentType,
  onRemoveContentType,
}: PipelineItemDrawerProps) {
  const [, setLocation] = useLocation();
  const [form, setForm] = useState<KanbanTask | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "script" | "review">("details");
  const [editorQuery, setEditorQuery] = useState("");
  const [showEditorDropdown, setShowEditorDropdown] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [showVideoControls, setShowVideoControls] = useState(false);
  const [showThumbnailControls, setShowThumbnailControls] = useState(false);
  const [newTypeInput, setNewTypeInput] = useState("");
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);
  const [showProcessDropdown, setShowProcessDropdown] = useState(false);
  const [showContentTypeDropdown, setShowContentTypeDropdown] = useState(false);
  const [cloudPickerTarget, setCloudPickerTarget] = useState<"video" | "thumbnail" | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [autoImportingDriveVideo, setAutoImportingDriveVideo] = useState(false);
  const [autoGeneratingThumbnail, setAutoGeneratingThumbnail] = useState(false);
  const videoFileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailFileInputRef = useRef<HTMLInputElement>(null);
  const scheduledAtInputRef = useRef<HTMLInputElement>(null);
  const editorDropdownRef = useRef<HTMLDivElement>(null);
  const processDropdownRef = useRef<HTMLDivElement>(null);
  const contentTypeDropdownRef = useRef<HTMLDivElement>(null);
  const autoImportedDriveFileIdRef = useRef<string | null>(null);
  const autoGeneratedThumbnailForRef = useRef<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation("pipelineDrawer");

  const { data: membersData } = useQuery<{ members: WorkspaceMember[] }>({
    queryKey: ["/api/agency/members"],
    enabled: open,
  });

  const { data: cloudStorageStatus } = useQuery<CloudStorageStatusResponse>({
    queryKey: ["cloud-storage-status"],
    queryFn: () => apiRequest("GET", "/api/integrations/cloud-storage/status"),
    enabled: open,
    staleTime: 30_000,
  });

  const members = membersData?.members ?? [];
  const filteredMembers = members.filter((m) =>
    (m.username ?? m.email).toLowerCase().includes(editorQuery.toLowerCase())
  );

  useEffect(() => {
    if (task) {
      setForm({ ...task });
      setActiveTab("details");
      setShowVideoControls(Boolean(task.fileUrl));
      setShowThumbnailControls(Boolean(task.thumbnailUrl));
      autoImportedDriveFileIdRef.current = null;
      autoGeneratedThumbnailForRef.current = task.thumbnailUrl ? (task.fileUrl ?? null) : null;
    }
  }, [task]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (editorDropdownRef.current && !editorDropdownRef.current.contains(e.target as Node))
        setShowEditorDropdown(false);
      if (processDropdownRef.current && !processDropdownRef.current.contains(e.target as Node))
        setShowProcessDropdown(false);
      if (
        contentTypeDropdownRef.current &&
        !contentTypeDropdownRef.current.contains(e.target as Node)
      ) {
        setShowContentTypeDropdown(false);
        setShowNewTypeInput(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isGoogleDriveConnected = Boolean(cloudStorageStatus?.google_drive?.connected);

  // ── VP-107: schedule/publish this card via the existing Scheduler integration ──
  const [, navigate] = useLocation();
  const [scheduleTargetKey, setScheduleTargetKey] = useState("");
  const [scheduling, setScheduling] = useState(false);
  interface SchedPage {
    id: string;
    name: string;
    instagramBusinessAccount?: { id: string; name?: string; username?: string };
  }
  interface SchedTarget {
    key: string;
    platform: "facebook" | "instagram";
    pageId: string;
    pageName: string;
    igAccountId: string;
    igAccountName: string;
    label: string;
  }
  const { data: schedPagesData } = useQuery<{ pages: SchedPage[] }>({
    queryKey: ["/api/scheduler/pages"],
    queryFn: () => apiRequest("GET", "/api/scheduler/pages"),
    enabled: open,
    staleTime: 60_000,
  });
  const scheduleTargets: SchedTarget[] = (schedPagesData?.pages ?? []).flatMap((p) => {
    const fb: SchedTarget = {
      key: `fb:${p.id}`,
      platform: "facebook",
      pageId: p.id,
      pageName: p.name,
      igAccountId: "",
      igAccountName: "",
      label: `FB: ${p.name}`,
    };
    const ig = p.instagramBusinessAccount?.id;
    return ig
      ? [
          fb,
          {
            key: `ig:${ig}`,
            platform: "instagram",
            pageId: p.id,
            pageName: p.name,
            igAccountId: ig,
            igAccountName:
              p.instagramBusinessAccount?.username || p.instagramBusinessAccount?.name || "",
            label: `IG: ${p.instagramBusinessAccount?.username || p.instagramBusinessAccount?.name || p.name}`,
          } as SchedTarget,
        ]
      : [fb];
  });

  // VP-299: link referencyjny musi przejść dokładnie tę samą walidację co backend.
  // Cały board leci jednym snapshotem, więc jeden zły link blokuje zapis wszystkich kart.
  const referenceUrlError =
    form && !isPipelineReferenceUrl(form.referenceUrl ?? "") ? REFERENCE_URL_ERROR : null;

  /** Zwraca `true` (i pokazuje toast), gdy zapis trzeba wstrzymać. */
  const blockedByReferenceUrl = (): boolean => {
    if (!referenceUrlError) return false;
    toast({
      title: "Niepoprawny link referencyjny",
      description: REFERENCE_URL_ERROR,
      variant: "destructive",
    });
    return true;
  };

  const handleSchedule = async (publishNow: boolean): Promise<void> => {
    if (!form) return;
    if (blockedByReferenceUrl()) return;
    const target = scheduleTargets.find((t) => t.key === scheduleTargetKey);
    if (!target) {
      toast({ title: "Wybierz konto do publikacji", variant: "destructive" });
      return;
    }
    if (!publishNow && !form.scheduledAt) {
      toast({ title: "Ustaw datę zaplanowania", variant: "destructive" });
      return;
    }
    setScheduling(true);
    try {
      const caption =
        [form.title?.trim(), form.description?.trim()].filter(Boolean).join("\n\n") ||
        form.title?.trim() ||
        "Post";
      const mediaUrls = [form.fileUrl, form.imageUrl, form.thumbnailUrl]
        .filter((u): u is string => !!u && /^https?:\/\//.test(u))
        .slice(0, 1);
      const mediaType =
        form.fileUrl && /^https?:\/\//.test(form.fileUrl)
          ? "video"
          : mediaUrls.length
            ? "image"
            : "text";
      const payload = {
        platform: target.platform,
        status: publishNow ? "draft" : "scheduled",
        caption,
        mediaType,
        mediaUrls,
        pageId: target.pageId,
        pageName: target.pageName,
        igAccountId: target.igAccountId || undefined,
        igAccountName: target.igAccountName || undefined,
        scheduledAt: publishNow ? undefined : form.scheduledAt,
      };
      const created = (await apiRequest("POST", "/api/scheduler", payload)) as {
        post: { id: string };
      };
      const postId = created.post.id;
      if (publishNow) {
        const pub = (await apiRequest("POST", `/api/scheduler/${postId}/publish`)) as {
          post?: { metaPostId?: string };
        };
        onSave({
          ...form,
          scheduledPostId: postId,
          scheduledPlatform: target.platform,
          publishedPostId: pub.post?.metaPostId,
          publishedAt: new Date().toISOString(),
          process: "opublikowane",
        });
        toast({ title: "Opublikowano ✨" });
      } else {
        onSave({
          ...form,
          scheduledPostId: postId,
          scheduledPlatform: target.platform,
          process: "zaplanowane",
        });
        toast({
          title: "Zaplanowano",
          description: "Materiał trafi do publikacji o ustawionej dacie.",
        });
      }
      onClose();
    } catch (e) {
      toast({
        title: "Nie udało się",
        description: e instanceof Error ? e.message.replace(/^\d+:\s*/, "") : "Spróbuj ponownie",
        variant: "destructive",
      });
    } finally {
      setScheduling(false);
    }
  };

  const update = <K extends keyof KanbanTask>(key: K, value: KanbanTask[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const patchForm = (patch: Partial<KanbanTask>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const maybeImportManualCloudVideo = async (
    task: KanbanTask,
    options?: { silentFailure?: boolean; silentSuccess?: boolean }
  ): Promise<KanbanTask> => {
    const fileUrl = task.fileUrl?.trim();
    const cloudLink = getManualCloudVideoLink(fileUrl);

    if (!cloudLink || !fileUrl) {
      return task;
    }

    try {
      const importedFromUrl = (await apiRequest("POST", "/api/scheduler/import-remote-media", {
        url: fileUrl,
      })) as RemoteImportedMedia;

      if (!options?.silentSuccess) {
        toast({
          title: t("toasts.videoImportedFrom", {
            provider: getCloudVideoLabel(cloudLink.provider),
          }),
          description: importedFromUrl.originalName,
        });
      }

      return {
        ...task,
        fileUrl: importedFromUrl.url,
      };
    } catch (publicImportError) {
      if (cloudLink.provider !== "google_drive" || !cloudLink.fileId) {
        if (!options?.silentFailure) {
          const publicImportMessage =
            publicImportError instanceof Error ? publicImportError.message : "";
          toast({
            title: t("toasts.importFailed"),
            description: publicImportMessage.includes("status")
              ? t("toasts.dropboxNotDirect")
              : t("toasts.dropboxFallback"),
            variant: "destructive",
          });
        }
        return task;
      }

      try {
        const imported = (await apiRequest(
          "POST",
          "/api/integrations/cloud-storage/import/google_drive",
          {
            fileId: cloudLink.fileId,
          }
        )) as ImportedFile;

        if (!options?.silentSuccess) {
          toast({
            title: t("toasts.driveImported"),
            description: imported.originalName,
          });
        }

        return {
          ...task,
          fileUrl: imported.path,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : "";
        const needsDriveConnection = message.includes("404");
        const publicImportMessage =
          publicImportError instanceof Error ? publicImportError.message : "";

        if (!options?.silentFailure) {
          toast({
            title: t("toasts.driveFailed"),
            description: needsDriveConnection
              ? t("toasts.driveNotPublic")
              : publicImportMessage.includes("publicznie")
                ? t("toasts.driveLoginOnly")
                : t("toasts.driveFallbackLink"),
            variant: "destructive",
          });
        }

        return task;
      }
    }
  };

  /** @returns `false`, gdy zapis wstrzymano (zły link) — wtedy nie zamykamy szuflady. */
  const handleSave = async (): Promise<boolean> => {
    if (!form) return true;

    // VP-299: nie zapisujemy śmieciowego linku referencyjnego — backend i tak by go odrzucił.
    if (blockedByReferenceUrl()) return false;
    if (isSaving) return true;

    setIsSaving(true);
    try {
      const imported = await maybeImportManualCloudVideo(form);
      // Sam whitespace to dla użytkownika puste pole, a dla backendu błąd walidacji.
      const normalized = {
        ...imported,
        referenceUrl: imported.referenceUrl?.trim() || undefined,
      };
      setForm(normalized);
      onSave(normalized);
      return true;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDriveFileSelected = (file: SelectedCloudFile) => {
    if (cloudPickerTarget !== "thumbnail") return;

    const url = toDriveFileUrl(file.fileId);
    patchForm({ thumbnailUrl: url, imageUrl: url });
    setShowThumbnailControls(true);
    toast({ title: t("toasts.thumbnailLinked"), description: file.originalName });
    setCloudPickerTarget(null);
  };

  const handleDriveVideoImported = (file: ImportedFile) => {
    if (cloudPickerTarget !== "video") return;

    update("fileUrl", file.path);
    setShowVideoControls(true);
    toast({
      title: t("toasts.videoImportedCloud"),
      description: file.originalName,
    });
    setCloudPickerTarget(null);
  };

  const uploadAsset = async (file: File, expectedType: "video" | "image"): Promise<string> => {
    try {
      const folder = expectedType === "video" ? "pipeline-videos" : "pipeline-thumbnails";
      const presigned = (await apiRequest("POST", "/api/storage/presign", {
        filename: file.name,
        mimeType: file.type,
        folder,
      })) as { uploadUrl: string; publicUrl: string; key: string };

      const putRes = await fetch(presigned.uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!putRes.ok) {
        const text = await getResponseText(putRes);
        throw new Error(text || t("toasts.uploadRejected"));
      }

      await apiRequest("POST", "/api/storage/register-uploaded-asset", {
        key: presigned.key,
        filename: file.name,
        mimeType: file.type,
        fileSizeBytes: file.size,
        bucketFolder: folder,
        sourceType:
          expectedType === "video"
            ? "pipeline_presigned_video_upload"
            : "pipeline_presigned_thumbnail_upload",
      });

      return presigned.publicUrl;
    } catch (presignError) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const data = (await uploadFile("/api/scheduler/upload-media", formData)) as {
          url: string;
          mediaType: "image" | "video";
        };
        if (data.mediaType !== expectedType) {
          throw new Error(
            expectedType === "video" ? t("toasts.selectVideo") : t("toasts.selectImage")
          );
        }
        return data.url;
      } catch (fallbackError) {
        if (fallbackError instanceof Error && !fallbackError.message.includes("Unexpected token")) {
          throw fallbackError;
        }
        if (presignError instanceof Error) {
          throw presignError;
        }
        throw fallbackError;
      }
    }
  };

  const handleVideoUpload = async (file: File) => {
    setUploadingVideo(true);
    try {
      const publicUrl = await uploadAsset(file, "video");
      update("fileUrl", publicUrl);
      setShowVideoControls(true);
      toast({ title: t("toasts.videoAdded"), description: file.name });
    } catch (error) {
      toast({
        title: t("toasts.uploadAgain"),
        description: error instanceof Error ? error.message : t("toasts.uploadFailed"),
        variant: "destructive",
      });
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbnailUpload = async (file: File) => {
    setUploadingThumbnail(true);
    try {
      const publicUrl = await uploadAsset(file, "image");
      patchForm({ thumbnailUrl: publicUrl, imageUrl: publicUrl });
      setShowThumbnailControls(true);
      toast({ title: t("toasts.thumbnailAdded"), description: file.name });
    } catch (error) {
      toast({
        title: t("toasts.uploadAgain"),
        description: error instanceof Error ? error.message : t("toasts.uploadFailed"),
        variant: "destructive",
      });
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const openScheduledPicker = () => {
    const input = scheduledAtInputRef.current;
    if (!input) return;
    input.showPicker?.();
    input.focus();
  };

  useEffect(() => {
    if (!open || !form?.fileUrl) {
      setAutoImportingDriveVideo(false);
      autoImportedDriveFileIdRef.current = null;
      return;
    }

    const fileUrl = form.fileUrl.trim();
    const cloudLink = getManualCloudVideoLink(fileUrl);
    const importFingerprint =
      cloudLink?.provider === "google_drive"
        ? `${cloudLink.provider}:${cloudLink.fileId}`
        : cloudLink?.provider;

    if (!cloudLink || !importFingerprint) {
      setAutoImportingDriveVideo(false);
      if (!cloudLink) {
        autoImportedDriveFileIdRef.current = null;
      }
      return;
    }

    if (autoImportedDriveFileIdRef.current === importFingerprint) {
      return;
    }

    const timer = window.setTimeout(async () => {
      setAutoImportingDriveVideo(true);
      autoImportedDriveFileIdRef.current = importFingerprint;

      try {
        const importedTask = await maybeImportManualCloudVideo(
          { ...form, fileUrl },
          { silentFailure: true, silentSuccess: true }
        );
        const importedUrl = importedTask.fileUrl;

        if (!importedUrl || importedUrl === fileUrl) {
          autoImportedDriveFileIdRef.current = null;
          return;
        }

        setForm((prev) => {
          if (!prev || prev.fileUrl?.trim() !== fileUrl) return prev;
          return {
            ...prev,
            fileUrl: importedUrl,
          };
        });

        toast({
          title: t("toasts.autoImportedFrom", { provider: getCloudVideoLabel(cloudLink.provider) }),
          description: t("toasts.readyForReview"),
        });
      } catch {
        autoImportedDriveFileIdRef.current = null;
      } finally {
        setAutoImportingDriveVideo(false);
      }
    }, 900);

    return () => window.clearTimeout(timer);
    // Celowo weze zaleznosci: efekt ma wystartowac tylko przy zmianie pliku, nie przy kazdej edycji karty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.fileUrl, toast]);

  useEffect(() => {
    if (!open || !form?.fileUrl) {
      setAutoGeneratingThumbnail(false);
      return;
    }

    const videoUrl = form.fileUrl.trim();
    const hasThumbnail = Boolean(form.thumbnailUrl);
    const isPendingCloudVideo = Boolean(getManualCloudVideoLink(videoUrl));

    if (hasThumbnail || isPendingCloudVideo || isYouTubeUrl(videoUrl)) {
      setAutoGeneratingThumbnail(false);
      if (!hasThumbnail) {
        autoGeneratedThumbnailForRef.current = null;
      }
      return;
    }

    if (autoGeneratedThumbnailForRef.current === videoUrl) {
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setAutoGeneratingThumbnail(true);
      autoGeneratedThumbnailForRef.current = videoUrl;

      try {
        let publicUrl: string;

        try {
          const generated = (await apiRequest("POST", "/api/scheduler/generate-thumbnail", {
            videoUrl,
            captureSeconds: THUMBNAIL_CAPTURE_SECONDS,
          })) as { url: string };
          publicUrl = generated.url;
        } catch {
          const thumbnailFile = await captureVideoThumbnail(videoUrl);
          publicUrl = await uploadAsset(thumbnailFile, "image");
        }

        if (cancelled) return;

        setForm((prev) => {
          if (!prev || prev.fileUrl?.trim() !== videoUrl || prev.thumbnailUrl) return prev;
          return {
            ...prev,
            thumbnailUrl: publicUrl,
            imageUrl: publicUrl,
          };
        });
        setShowThumbnailControls(true);

        toast({
          title: t("toasts.thumbnailGenerated"),
          description: t("toasts.thumbnailAutoSet"),
        });
      } catch {
        autoGeneratedThumbnailForRef.current = null;
      } finally {
        if (!cancelled) {
          setAutoGeneratingThumbnail(false);
        }
      }
    }, 700);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // Celowo weze zaleznosci: efekt ma wystartowac tylko przy zmianie pliku/miniatury.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.fileUrl, form?.thumbnailUrl, toast]);

  if (!form) return null;

  const selectedPlatforms = new Set(form.socialPlatforms ?? []);
  const trimmedReferenceUrl = form.referenceUrl?.trim() ?? "";

  const togglePlatform = (platform: PipelineSocialPlatform) => {
    const next = new Set(selectedPlatforms);
    if (next.has(platform)) next.delete(platform);
    else next.add(platform);
    // Keep the canonical order so the board stripe does not depend on click order.
    const ordered = PIPELINE_SOCIAL_PLATFORMS.filter((meta) => next.has(meta.id)).map(
      (meta) => meta.id
    );
    update("socialPlatforms", ordered.length > 0 ? ordered : undefined);
  };

  const currentCloudLink = getManualCloudVideoLink(form.fileUrl);
  const isImportedVideo = Boolean(form.fileUrl && !currentCloudLink && !isYouTubeUrl(form.fileUrl));
  const isDriveVideoLink = currentCloudLink?.provider === "google_drive";
  const isDropboxVideoLink = currentCloudLink?.provider === "dropbox";
  const videoStatusMessage = autoImportingDriveVideo
    ? t("status.importing", {
        provider: getCloudVideoLabel(currentCloudLink?.provider ?? "google_drive"),
      })
    : autoGeneratingThumbnail
      ? t("status.generatingThumbnail")
      : isDriveVideoLink && isGoogleDriveConnected
        ? t("status.driveConnected")
        : isDriveVideoLink && !isGoogleDriveConnected
          ? t("status.driveNotConnected")
          : isDropboxVideoLink
            ? t("status.dropboxDetected")
            : isImportedVideo
              ? t("status.videoReady")
              : null;

  // ── Process helpers ──────────────────────────────────────

  const selectedProcess = PROCESS_OPTIONS.find((o) => o.value === form.process);

  // ── Content type helpers ─────────────────────────────────

  const selectedContentType = form.contentTypes?.[0];

  const handleSelectContentType = (tag: string) => {
    update("contentTypes", [tag]);
    setShowContentTypeDropdown(false);
    setShowNewTypeInput(false);
  };

  const handleAddNewType = () => {
    const trimmed = newTypeInput.trim().toUpperCase();
    if (!trimmed) return;
    onAddContentType(trimmed);
    handleSelectContentType(trimmed);
    setNewTypeInput("");
    setShowNewTypeInput(false);
  };

  const handleRemoveType = (e: React.MouseEvent, tag: string) => {
    e.stopPropagation();
    if (selectedContentType === tag) update("contentTypes", []);
    onRemoveContentType(tag);
  };

  // ── Render ───────────────────────────────────────────────

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (v) return;
        // Zamknięcie = zapis. Przy złym linku zostajemy otwarci (toast w handleSave),
        // żeby nie stracić po cichu wszystkich pozostałych zmian w karcie.
        void handleSave()
          .then((saved) => {
            if (saved) onClose();
          })
          .catch(() => onClose());
      }}
    >
      <SheetContent
        side="right"
        className={cn(
          "bg-background border-border text-foreground flex flex-col gap-0 p-0 transition-all duration-300",
          activeTab === "review" ? "w-full sm:max-w-[900px]" : "w-full sm:max-w-[560px]"
        )}
      >
        <SheetHeader className="px-6 pt-5 pb-0 shrink-0">
          <SheetTitle className="text-white text-sm font-semibold truncate mb-3">
            {form.title}
          </SheetTitle>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-white/[0.06]">
            {(
              [
                { id: "details", label: t("tabs.details"), icon: Settings2 },
                { id: "script", label: t("tabs.script"), icon: FileText },
                { id: "review", label: "Review", icon: Film },
              ] as const
            ).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors",
                  activeTab === id
                    ? "border-red-500 text-white"
                    : "border-transparent text-white/40 hover:text-white/70"
                )}
              >
                <Icon size={12} />
                {label}
              </button>
            ))}
          </div>
        </SheetHeader>

        {/* ── REVIEW TAB ── */}
        {activeTab === "review" && (
          <div className="flex-1 overflow-hidden px-6 py-4 min-h-0">
            <VideoReviewTab taskId={form.id} videoUrl={form.fileUrl} />
          </div>
        )}

        {/* ── DETAILS TAB ── */}
        {activeTab === "details" && (
          <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
            {/* Task ID — for sharing / referencing */}
            <div className="flex items-center justify-between -mb-3">
              <span className="text-[9px] uppercase tracking-wider text-white/30 font-semibold font-mono">
                ID: {form.id.slice(0, 8)}…{form.id.slice(-4)}
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(form.id);
                    toast({ title: "Skopiowano ID", description: form.id });
                  } catch {
                    // ignore
                  }
                }}
                className="text-[9px] text-white/40 hover:text-white/70 transition-colors"
              >
                Kopiuj ID
              </button>
            </div>

            {/* Nazwa */}
            <Field label={t("fields.title")}>
              <input
                type="text"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
              />
            </Field>

            {/* Platformy docelowe (VP-299) — sterują kolorem kafelka na boardzie */}
            <Field label="Platformy">
              <div className="flex flex-wrap gap-1.5">
                {PIPELINE_SOCIAL_PLATFORMS.map((platform) => {
                  const isActive = selectedPlatforms.has(platform.id);
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => togglePlatform(platform.id)}
                      className={cn(
                        "flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-all",
                        isActive
                          ? platform.chipClass
                          : "border-white/[0.08] text-white/40 hover:text-white hover:border-white/[0.15]"
                      )}
                    >
                      <span
                        className={cn(
                          "h-2 w-2 rounded-full",
                          isActive ? platform.stripeClass : "bg-white/20"
                        )}
                      />
                      {platform.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* Approval gate (Sprint 3, transcript 2026-05) */}
            <Field label="Akceptacja">
              <div className="flex flex-col gap-2">
                <div className="flex gap-1.5">
                  {(
                    [
                      { id: undefined, label: "Brak", cls: "border-white/[0.08] text-white/55" },
                      {
                        id: "pending",
                        label: "⏳ Do akceptacji",
                        cls: "border-amber-500/40 bg-amber-500/10 text-amber-200",
                      },
                      {
                        id: "approved",
                        label: "✓ Zatwierdzone",
                        cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
                      },
                      {
                        id: "rejected",
                        label: "✕ Odrzucone",
                        cls: "border-red-500/40 bg-red-500/10 text-red-200",
                      },
                    ] as const
                  ).map((opt) => {
                    const isActive = form.approvalStatus === opt.id;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => {
                          update("approvalStatus", opt.id);
                          if (opt.id === "approved") {
                            update("approvedAt", new Date().toISOString());
                          } else if (opt.id !== "rejected") {
                            update("rejectionReason", undefined);
                          }
                        }}
                        className={`px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-all ${
                          isActive
                            ? opt.cls
                            : "border-white/[0.08] text-white/40 hover:text-white hover:border-white/[0.15]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {form.approvalStatus === "rejected" && (
                  <input
                    type="text"
                    placeholder="Powód odrzucenia (opcjonalnie)"
                    value={form.rejectionReason ?? ""}
                    onChange={(e) => update("rejectionReason", e.target.value || undefined)}
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-red-500/40"
                  />
                )}
                {form.approvalStatus === "approved" && form.approvedAt && (
                  <span className="text-[10px] text-emerald-300">
                    Zatwierdzono:{" "}
                    {new Date(form.approvedAt).toLocaleString("pl-PL", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </span>
                )}
              </div>
            </Field>

            {/* Process / Status — custom dropdown */}
            <Field label="Process / Status">
              <div className="relative" ref={processDropdownRef}>
                <button
                  onClick={() => setShowProcessDropdown((v) => !v)}
                  className="w-full flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-left hover:border-white/20 transition-colors"
                >
                  {selectedProcess ? (
                    <span className="flex items-center gap-2 text-white">
                      <span
                        className={cn("w-2 h-2 rounded-full shrink-0", selectedProcess.color)}
                      />
                      {t(`process.${selectedProcess.value}`)}
                    </span>
                  ) : (
                    <span className="text-white/30">{t("buttons.selectProcess")}</span>
                  )}
                  <div className="flex items-center gap-1.5">
                    {selectedProcess && (
                      <span
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          update("process", undefined);
                        }}
                        className="text-white/30 hover:text-white/60 transition-colors"
                      >
                        <X size={12} />
                      </span>
                    )}
                    <ChevronDown size={14} className="text-white/30" />
                  </div>
                </button>

                {showProcessDropdown && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/[0.08] bg-popover shadow-xl overflow-hidden">
                    {PROCESS_OPTIONS.map((opt) => (
                      <div
                        key={opt.value}
                        onClick={() => {
                          update("process", opt.value);
                          setShowProcessDropdown(false);
                        }}
                        className={cn(
                          "flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors",
                          form.process === opt.value
                            ? "bg-white/[0.08] text-white"
                            : "text-white/70 hover:bg-white/[0.06] hover:text-white"
                        )}
                      >
                        <span className={cn("w-2 h-2 rounded-full shrink-0", opt.color)} />
                        {t(`process.${opt.value}`)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Field>

            {/* Odpowiedzialny za montaż */}
            <Field label={t("fields.editor")}>
              <div className="relative" ref={editorDropdownRef}>
                <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 focus-within:border-white/20">
                  <User size={14} className="text-white/30 shrink-0" />
                  <input
                    type="text"
                    value={showEditorDropdown ? editorQuery : (form.editorName ?? "")}
                    onChange={(e) => {
                      setEditorQuery(e.target.value);
                      update("editorName", e.target.value);
                      update("editorUserId", undefined);
                    }}
                    onFocus={() => {
                      setEditorQuery(form.editorName ?? "");
                      setShowEditorDropdown(true);
                    }}
                    placeholder={t("placeholders.editorSearch")}
                    className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                  />
                  {members.length > 0 && (
                    <ChevronDown
                      size={14}
                      className="text-white/30 shrink-0 cursor-pointer"
                      onClick={() => setShowEditorDropdown((v) => !v)}
                    />
                  )}
                </div>
                {showEditorDropdown && members.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/[0.08] bg-popover shadow-xl overflow-hidden">
                    {filteredMembers.length === 0 && (
                      <div className="px-3 py-2 text-xs text-white/30">
                        {t("buttons.noResults")}
                      </div>
                    )}
                    {filteredMembers.map((m) => (
                      <button
                        key={m.id}
                        className="w-full text-left px-3 py-2 text-xs text-white/70 hover:bg-white/[0.06] hover:text-white transition-colors"
                        onClick={() => {
                          update("editorUserId", m.id);
                          update("editorName", m.username ?? m.email);
                          setShowEditorDropdown(false);
                          setEditorQuery("");
                        }}
                      >
                        {m.username ?? m.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>

            {/* Rodzaj contentu — dropdown */}
            <Field label={t("fields.contentType")}>
              <div className="relative" ref={contentTypeDropdownRef}>
                <button
                  onClick={() => setShowContentTypeDropdown((v) => !v)}
                  className="w-full flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-left hover:border-white/20 transition-colors"
                >
                  {selectedContentType ? (
                    <span className="text-white">{selectedContentType}</span>
                  ) : (
                    <span className="text-white/30">{t("buttons.selectType")}</span>
                  )}
                  <div className="flex items-center gap-1.5">
                    {selectedContentType && (
                      <span
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          update("contentTypes", []);
                        }}
                        className="text-white/30 hover:text-white/60 transition-colors"
                      >
                        <X size={12} />
                      </span>
                    )}
                    <ChevronDown size={14} className="text-white/30" />
                  </div>
                </button>

                {showContentTypeDropdown && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/[0.08] bg-popover shadow-xl overflow-hidden">
                    {availableContentTypes.map((tag) => (
                      <div
                        key={tag}
                        className={cn(
                          "flex items-center justify-between px-3 py-2 text-sm cursor-pointer group transition-colors",
                          selectedContentType === tag
                            ? "bg-red-600/20 text-white"
                            : "text-white/70 hover:bg-white/[0.06] hover:text-white"
                        )}
                        onClick={() => handleSelectContentType(tag)}
                      >
                        <span>{tag}</span>
                        <button
                          onClick={(e) => handleRemoveType(e, tag)}
                          className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition-all"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                    <div className="border-t border-white/[0.06] p-2">
                      {showNewTypeInput ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            autoFocus
                            value={newTypeInput}
                            onChange={(e) => setNewTypeInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleAddNewType();
                              if (e.key === "Escape") {
                                setShowNewTypeInput(false);
                                setNewTypeInput("");
                              }
                            }}
                            placeholder={t("placeholders.newType")}
                            className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded px-2 py-1 text-xs text-white uppercase placeholder:text-white/20 placeholder:normal-case focus:outline-none focus:border-white/20"
                          />
                          <button
                            onClick={handleAddNewType}
                            className="text-white/40 hover:text-white p-1 transition-colors"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            onClick={() => {
                              setShowNewTypeInput(false);
                              setNewTypeInput("");
                            }}
                            className="text-white/40 hover:text-white p-1 transition-colors"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowNewTypeInput(true);
                          }}
                          className="w-full flex items-center gap-1.5 px-1 py-1 text-xs text-white/30 hover:text-white/60 transition-colors"
                        >
                          <Plus size={12} /> {t("buttons.addNewType")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Field>

            {/* Notatki (VP-299) — wolny tekst, odrębny od scenariusza */}
            <Field label="Notatki">
              <Textarea
                value={form.notes ?? ""}
                onChange={(e) => update("notes", e.target.value || undefined)}
                maxLength={PIPELINE_NOTES_MAX_LENGTH}
                placeholder="Uwagi do zadania, ustalenia, co poprawić…"
                className="min-h-[90px] bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 resize-y focus:border-white/20 focus-visible:ring-0"
              />
            </Field>

            {/* Link referencyjny (VP-299) — wzór / inspiracja do materiału */}
            <Field label="Link referencyjny">
              <div className="flex flex-col gap-1.5">
                <div
                  className={cn(
                    "flex items-center gap-2 bg-white/[0.04] border rounded-lg px-3 transition-colors focus-within:border-white/20",
                    referenceUrlError ? "border-red-500/40" : "border-white/[0.08]"
                  )}
                >
                  <Link2 size={14} className="text-white/30 shrink-0" />
                  <input
                    type="url"
                    inputMode="url"
                    value={form.referenceUrl ?? ""}
                    onChange={(e) => update("referenceUrl", e.target.value || undefined)}
                    maxLength={PIPELINE_REFERENCE_URL_MAX_LENGTH}
                    placeholder="https://… (wzór / inspiracja)"
                    className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-white/30 focus:outline-none"
                  />
                  {form.referenceUrl && (
                    <button
                      type="button"
                      onClick={() => update("referenceUrl", undefined)}
                      className="text-white/30 hover:text-white/60 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {referenceUrlError ? (
                  <span className="text-[10px] text-red-300">{referenceUrlError}</span>
                ) : (
                  trimmedReferenceUrl && (
                    <a
                      href={trimmedReferenceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-white/40 hover:text-white/70 underline truncate"
                    >
                      Otwórz link
                    </a>
                  )
                )}
              </div>
            </Field>

            {/* Data zaplanowania — datetime-local */}
            <Field label={t("fields.scheduledAt")}>
              <div
                className="relative flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 hover:border-white/20 transition-colors focus-within:border-white/20"
                onClick={openScheduledPicker}
              >
                <CalendarDays size={14} className="text-white/30 shrink-0" />
                <input
                  ref={scheduledAtInputRef}
                  type="datetime-local"
                  value={form.scheduledAt ? toDatetimeLocal(form.scheduledAt) : ""}
                  onChange={(e) =>
                    update(
                      "scheduledAt",
                      e.target.value ? new Date(e.target.value).toISOString() : undefined
                    )
                  }
                  onFocus={openScheduledPicker}
                  className="flex-1 bg-transparent py-2 text-sm text-white focus:outline-none [color-scheme:light] dark:[color-scheme:dark] cursor-pointer"
                />
                {form.scheduledAt && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      update("scheduledAt", undefined);
                    }}
                    className="text-white/30 hover:text-white/60 transition-colors ml-1"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </Field>

            {/* VP-107: Publikacja przez Scheduler */}
            <Field label="Publikacja (Scheduler)">
              {form.publishedPostId || form.process === "opublikowane" ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2.5">
                  <span className="text-xs text-emerald-200/90">
                    ✓ Opublikowane{form.scheduledPlatform ? ` (${form.scheduledPlatform})` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate("/content-analytics")}
                    className="text-xs text-emerald-300 hover:text-emerald-100 underline shrink-0"
                  >
                    Zobacz statystyki
                  </button>
                </div>
              ) : form.scheduledPostId ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5">
                  <span className="text-xs text-amber-200/90">
                    ⏳ Zaplanowane{form.scheduledPlatform ? ` na ${form.scheduledPlatform}` : ""}
                    {form.scheduledAt
                      ? ` — ${new Date(form.scheduledAt).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}`
                      : ""}
                    . Opublikuje się automatycznie.
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  <select
                    value={scheduleTargetKey}
                    onChange={(e) => setScheduleTargetKey(e.target.value)}
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white [color-scheme:light] dark:[color-scheme:dark] focus:outline-none focus:border-white/20"
                  >
                    <option value="">Wybierz konto…</option>
                    {scheduleTargets.map((tg) => (
                      <option key={tg.key} value={tg.key}>
                        {tg.label}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={scheduling || !scheduleTargetKey || Boolean(referenceUrlError)}
                      title={referenceUrlError ?? undefined}
                      onClick={() => handleSchedule(false)}
                      className="flex-1 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black text-xs font-semibold disabled:opacity-40"
                    >
                      {scheduling ? "Pracuję…" : "Zaplanuj na datę"}
                    </button>
                    <button
                      type="button"
                      disabled={scheduling || !scheduleTargetKey || Boolean(referenceUrlError)}
                      title={referenceUrlError ?? undefined}
                      onClick={() => handleSchedule(true)}
                      className="flex-1 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-black text-xs font-semibold disabled:opacity-40"
                    >
                      {scheduling ? "Pracuję…" : "Publikuj teraz"}
                    </button>
                  </div>
                  {scheduleTargets.length === 0 && (
                    <p className="text-[10px] text-white/35">
                      Podłącz konto (Meta) w ustawieniach, aby publikować.
                    </p>
                  )}
                </div>
              )}
            </Field>

            {/* Plik video */}
            <Field label={t("fields.videoFile")}>
              {!showVideoControls && !form.fileUrl ? (
                <button
                  type="button"
                  onClick={() => setShowVideoControls(true)}
                  className="flex items-center justify-center gap-2 w-full h-11 rounded-lg border border-dashed border-white/[0.08] text-white/50 hover:border-white/20 hover:text-white/70 transition-all text-xs"
                >
                  <Upload size={14} />
                  {t("buttons.addVideo")}
                </button>
              ) : (
                <>
                  <input
                    ref={videoFileInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleVideoUpload(file);
                      e.currentTarget.value = "";
                    }}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => videoFileInputRef.current?.click()}
                      disabled={uploadingVideo}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-white/70 hover:border-white/20 hover:text-white transition-colors disabled:opacity-50"
                    >
                      <Upload size={13} />
                      {uploadingVideo ? t("buttons.uploading") : t("buttons.selectFile")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCloudPickerTarget("video")}
                      title={t("buttons.importCloudTitle")}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-white/70 hover:border-white/20 hover:text-white transition-colors"
                    >
                      <HardDrive size={13} />
                      {t("buttons.importCloud")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (form.fileUrl) {
                          update("fileUrl", undefined);
                        }
                        setShowVideoControls(false);
                      }}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-xs text-white/40 hover:text-white/70 transition-colors"
                    >
                      <X size={13} />
                      {form.fileUrl ? t("buttons.removeVideo") : t("buttons.cancel")}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 focus-within:border-white/20">
                    <Link2 size={14} className="text-white/30 shrink-0" />
                    <input
                      type="url"
                      value={form.fileUrl ?? ""}
                      onChange={(e) => update("fileUrl", e.target.value || undefined)}
                      placeholder={t("placeholders.videoUrl")}
                      className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                    />
                    {form.fileUrl && (
                      <button
                        onClick={() => update("fileUrl", undefined)}
                        className="text-white/30 hover:text-white/60 transition-colors shrink-0"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                  <div className="px-1 text-[11px] text-white/35">{videoStatusMessage}</div>
                </>
              )}
            </Field>

            {/* Miniatura */}
            <Field label={t("fields.thumbnail")}>
              {!showThumbnailControls && !form.thumbnailUrl ? (
                <button
                  type="button"
                  onClick={() => setShowThumbnailControls(true)}
                  className="flex items-center justify-center gap-2 w-full h-11 rounded-lg border border-dashed border-white/[0.08] text-white/50 hover:border-white/20 hover:text-white/70 transition-all text-xs"
                >
                  <Upload size={14} />
                  {t("buttons.addThumbnail")}
                </button>
              ) : (
                <>
                  <input
                    ref={thumbnailFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleThumbnailUpload(file);
                      e.currentTarget.value = "";
                    }}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => thumbnailFileInputRef.current?.click()}
                      disabled={uploadingThumbnail}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-white/70 hover:border-white/20 hover:text-white transition-colors disabled:opacity-50"
                    >
                      <Upload size={13} />
                      {uploadingThumbnail
                        ? t("buttons.uploadingThumbnail")
                        : t("buttons.selectFile")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCloudPickerTarget("thumbnail")}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-white/70 hover:border-white/20 hover:text-white transition-colors"
                    >
                      <HardDrive size={13} />
                      {t("buttons.selectFromDrive")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (form.thumbnailUrl) {
                          patchForm({ thumbnailUrl: undefined, imageUrl: undefined });
                        }
                        setShowThumbnailControls(false);
                      }}
                      className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-xs text-white/40 hover:text-white/70 transition-colors"
                    >
                      <X size={13} />
                      {form.thumbnailUrl ? t("buttons.removeThumbnail") : t("buttons.cancel")}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 focus-within:border-white/20">
                    <Link2 size={14} className="text-white/30 shrink-0" />
                    <input
                      type="url"
                      value={form.thumbnailUrl ?? ""}
                      onChange={(e) =>
                        patchForm({
                          thumbnailUrl: e.target.value || undefined,
                          imageUrl: e.target.value || undefined,
                        })
                      }
                      placeholder={t("placeholders.thumbnailUrl")}
                      className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                    />
                    {form.thumbnailUrl && (
                      <button
                        onClick={() => patchForm({ thumbnailUrl: undefined, imageUrl: undefined })}
                        className="text-white/30 hover:text-white/60 transition-colors shrink-0"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                  {form.thumbnailUrl && (
                    <div className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-white/[0.03] p-2">
                      <div className="flex h-16 w-24 items-center justify-center rounded bg-black/20 p-1 shrink-0">
                        <img
                          src={resolveImagePreviewUrl(form.thumbnailUrl)}
                          alt={t("imageAlt.thumbnail")}
                          className="max-h-full max-w-full rounded object-contain"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-white/70">{t("buttons.thumbnailPreview")}</p>
                        <p className="text-[11px] text-white/30 truncate">{form.thumbnailUrl}</p>
                      </div>
                      <button
                        onClick={() => patchForm({ thumbnailUrl: undefined, imageUrl: undefined })}
                        className="self-start rounded-full bg-black/30 p-1 text-white/50 hover:text-white/80 transition-colors"
                      >
                        <X size={12} className="text-white" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </Field>
          </div>
        )}

        {/* ── SCRIPT TAB ── */}
        {activeTab === "script" && (
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <Field label={t("fields.script")}>
              <Textarea
                value={form.script ?? ""}
                onChange={(e) => update("script", e.target.value || undefined)}
                placeholder={t("placeholders.script")}
                className="min-h-[420px] bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 resize-y focus:border-white/20 focus-visible:ring-0"
              />
            </Field>
          </div>
        )}

        {/* Footer — tylko poza review */}
        {activeTab !== "review" && (
          <div className="shrink-0 px-6 py-4 mb-20 border-t border-white/[0.06] flex justify-between items-center gap-3">
            <button
              onClick={async () => {
                await handleSave();
                setLocation("/content-creator?tab=scheduler");
                onClose();
              }}
              disabled={
                isSaving || form.approvalStatus !== "approved" || Boolean(referenceUrlError)
              }
              title={
                referenceUrlError
                  ? referenceUrlError
                  : form.approvalStatus !== "approved"
                    ? "Zatwierdź zadanie zanim zaplanujesz publikację"
                    : "Przejdź do schedulera"
              }
              className="px-4 py-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <CalendarDays className="w-4 h-4" /> Zaplanuj publikację
            </button>
            <div className="flex items-center gap-2">
              {onDelete && (
                <button
                  onClick={() => onDelete(form.id)}
                  disabled={isSaving}
                  title="Usuń to zadanie z pipeline"
                  className="px-3 py-2 rounded-lg border border-red-500/30 text-red-400 hover:text-red-200 hover:bg-red-500/10 text-sm font-medium transition-colors disabled:opacity-40 flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" /> Usuń
                </button>
              )}
              {onDuplicate && (
                <button
                  onClick={() => {
                    onDuplicate(form);
                    onClose();
                  }}
                  disabled={isSaving || Boolean(referenceUrlError)}
                  title={
                    referenceUrlError ??
                    "Utwórz kopię tego zadania (np. dla kolejnego odcinka serii)"
                  }
                  className="px-3 py-2 rounded-lg border border-white/[0.1] text-white/65 hover:text-white hover:bg-white/[0.04] text-sm font-medium transition-colors disabled:opacity-40"
                >
                  Duplikuj
                </button>
              )}
              <button
                onClick={() => {
                  handleSave().finally(onClose);
                }}
                disabled={isSaving || Boolean(referenceUrlError)}
                title={referenceUrlError ?? undefined}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors"
              >
                {isSaving ? t("buttons.saving") : t("buttons.save")}
              </button>
            </div>
          </div>
        )}
      </SheetContent>

      <CloudStoragePicker
        open={cloudPickerTarget !== null}
        onClose={() => setCloudPickerTarget(null)}
        onFileImported={handleDriveVideoImported}
        onFileSelected={handleDriveFileSelected}
        accept={cloudPickerTarget === "thumbnail" ? "image" : "video"}
        mode={cloudPickerTarget === "thumbnail" ? "select" : "import"}
      />
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-white/40 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
